import i18n from 'i18n';
import { ColGroupDef } from '@ag-grid-community/core';
import { CollectionFunction, HI2Interface, HI3Interface } from 'data/collectionFunction/collectionFunction.types';
import { stateValueFormatter } from 'utils/valueFormatters';
import { getFieldFromFirstInterfaceNameValueGetter } from 'utils/valueGetters';
import getFieldFromPropertyPathValueGetter from 'utils/valueGetters/getFieldFromPropertyPathValueGetter';
import getFieldOptionFromFirstInterfaceNameValueGetter from 'utils/valueGetters/getFieldOptionFromFirstInterfaceNameValueGetter';
import getNumberOfItemsFromFieldNameValueGetter from 'utils/valueGetters/getNumberOfItemsFromFieldNameValueGetter';
import { xcipioBehindNatValueGetter } from './utils/collectionFunctionGridValueGetters';

export enum CollectionFunctionMenuActionsType {
  Copy = 'copy',
  Delete = 'delete',
  Edit = 'edit',
  View = 'view'
}

export const collectionFunctionsColumnDefs: ColGroupDef[] = [
  {
    headerName: '',
    children: [
      {
        headerName: i18n.t('collectionFunction.grid.name'),
        field: 'name',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        tooltipField: 'name',
        width: 198,
        lockVisible: true,
      },
      {
        headerName: i18n.t('collectionFunction.grid.id'),
        field: 'decodedId',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        minWidth: 186,
      },
      {
        headerName: i18n.t('collectionFunction.grid.handoverProtocol'),
        field: 'tagName',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        tooltipField: 'tagName',
        minWidth: 186,
      },
      {
        headerName: i18n.t('collectionFunction.grid.lea'),
        field: 'cfGroup',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        minWidth: 186,
        tooltipValueGetter: getFieldFromPropertyPathValueGetter(['name']),
        valueGetter: getFieldFromPropertyPathValueGetter(['name']),
      },
      {
        headerName: i18n.t('collectionFunction.grid.behindNAT'),
        field: 'insideNat',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        minWidth: 143,
        tooltipValueGetter: xcipioBehindNatValueGetter,
        valueGetter: xcipioBehindNatValueGetter,
      },
      {
        headerName: i18n.t('collectionFunction.grid.cfId'),
        hide: true,
        field: 'cfid',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        tooltipField: 'id',
      },
      {
        headerName: i18n.t('collectionFunction.grid.numberOfIRI'),
        hide: true,
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        valueGetter: getNumberOfItemsFromFieldNameValueGetter<CollectionFunction, HI3Interface>('hi2Interfaces'),
        tooltipValueGetter: getNumberOfItemsFromFieldNameValueGetter<CollectionFunction, HI3Interface>('hi2Interfaces'),
      },
      {
        headerName: i18n.t('collectionFunction.grid.numberOfCC'),
        hide: true,
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        tooltipValueGetter: getNumberOfItemsFromFieldNameValueGetter<CollectionFunction, HI3Interface>('hi3Interfaces'),
        valueGetter: getNumberOfItemsFromFieldNameValueGetter<CollectionFunction, HI3Interface>('hi3Interfaces'),
      },
    ],
  },
  {
    headerName: i18n.t('collectionFunction.grid.interceptRelatedInformation'),
    children: [
      {
        headerName: i18n.t('collectionFunction.grid.transportProtocol'),
        field: 'transportType',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        minWidth: 147,
        tooltipValueGetter: getFieldFromFirstInterfaceNameValueGetter<CollectionFunction, HI2Interface>('hi2Interfaces'),
        valueGetter: getFieldFromFirstInterfaceNameValueGetter<CollectionFunction, HI2Interface>('hi2Interfaces'),
      },
      {
        headerName: i18n.t('collectionFunction.grid.ipAddress'),
        field: 'destIPAddr',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        minWidth: 147,
        tooltipValueGetter: getFieldOptionFromFirstInterfaceNameValueGetter<CollectionFunction, HI2Interface>('hi2Interfaces'),
        valueGetter: getFieldOptionFromFirstInterfaceNameValueGetter<CollectionFunction, HI2Interface>('hi2Interfaces'),
      },
      {
        headerName: i18n.t('collectionFunction.grid.port'),
        field: 'destPort',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        minWidth: 96,
        tooltipValueGetter: getFieldOptionFromFirstInterfaceNameValueGetter<CollectionFunction, HI2Interface>('hi2Interfaces'),
        valueGetter: getFieldOptionFromFirstInterfaceNameValueGetter<CollectionFunction, HI2Interface>('hi2Interfaces'),
      },
      {
        headerName: i18n.t('collectionFunction.grid.currentState'),
        field: 'state',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        minWidth: 143,
        tooltipValueGetter: getFieldOptionFromFirstInterfaceNameValueGetter<CollectionFunction, HI2Interface>('hi2Interfaces'),
        valueGetter: getFieldOptionFromFirstInterfaceNameValueGetter<CollectionFunction, HI2Interface>('hi2Interfaces'),
        valueFormatter: stateValueFormatter,

      },
      {
        headerName: i18n.t('collectionFunction.grid.configuredState'),
        field: 'reqState',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        minWidth: 151,
        tooltipValueGetter: getFieldOptionFromFirstInterfaceNameValueGetter<CollectionFunction, HI2Interface>('hi2Interfaces'),
        valueGetter: getFieldOptionFromFirstInterfaceNameValueGetter<CollectionFunction, HI2Interface>('hi2Interfaces'),
        valueFormatter: stateValueFormatter,
      },
    ],
  },
  {
    headerName: i18n.t('collectionFunction.grid.communicationContent'),
    children: [
      {
        headerName: i18n.t('collectionFunction.grid.transportProtocol'),
        field: 'transportType',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        minWidth: 147,
        tooltipValueGetter: getFieldFromFirstInterfaceNameValueGetter<CollectionFunction, HI3Interface>('hi3Interfaces'),
        valueGetter: getFieldFromFirstInterfaceNameValueGetter<CollectionFunction, HI3Interface>('hi3Interfaces'),
      },
      {
        headerName: i18n.t('collectionFunction.grid.ipAddress'),
        field: 'destIPAddr',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        minWidth: 147,
        tooltipValueGetter: getFieldOptionFromFirstInterfaceNameValueGetter<CollectionFunction, HI3Interface>('hi3Interfaces'),
        valueGetter: getFieldOptionFromFirstInterfaceNameValueGetter<CollectionFunction, HI3Interface>('hi3Interfaces'),
      },
      {
        headerName: i18n.t('collectionFunction.grid.port'),
        field: 'destPort',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        minWidth: 96,
        tooltipValueGetter: getFieldOptionFromFirstInterfaceNameValueGetter<CollectionFunction, HI3Interface>('hi3Interfaces'),
        valueGetter: getFieldOptionFromFirstInterfaceNameValueGetter<CollectionFunction, HI3Interface>('hi3Interfaces'),
      },
      {
        headerName: i18n.t('collectionFunction.grid.currentState'),
        field: 'state',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        minWidth: 96,
        tooltipValueGetter: getFieldOptionFromFirstInterfaceNameValueGetter<CollectionFunction, HI3Interface>('hi3Interfaces'),
        valueGetter: getFieldOptionFromFirstInterfaceNameValueGetter<CollectionFunction, HI3Interface>('hi3Interfaces'),
        valueFormatter: stateValueFormatter,
      },
      {
        headerName: i18n.t('collectionFunction.grid.configuredState'),
        field: 'reqState',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        minWidth: 96,
        tooltipValueGetter: getFieldOptionFromFirstInterfaceNameValueGetter<CollectionFunction, HI3Interface>('hi3Interfaces'),
        valueGetter: getFieldOptionFromFirstInterfaceNameValueGetter<CollectionFunction, HI3Interface>('hi3Interfaces'),
        valueFormatter: stateValueFormatter,
      },
    ],
  },
];
